import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Container, Dialog, DialogContent, DialogTitle, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, Modal, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ausbildungOptionDetails, ausbildungOptions } from "../../constants/ausbildungOptions";
import Meta from "../Meta/Meta";
import api from "lib/api/api";
import AusbildungInterestForm from "../Dashboard/StudentDashboard/AusbildungInterestForm";

const UniversityTrainingPage = ({ mode = "combined" }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isAuthenticated);
  const roleName = useSelector((state) => state.auth.user.roleName);
  const roleNames = useSelector((state) => state.auth.user.roleNames);
  const isStudent = [roleName, ...(Array.isArray(roleNames) ? roleNames : [])]
    .filter(Boolean)
    .some((role) => role.toUpperCase() === "STUDENT");
  const [activeTab, setActiveTab] = useState(mode === "ausbildung" ? 1 : 0);
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [showComingSoonTooltip, setShowComingSoonTooltip] = useState(false);
  const [managedCategories, setManagedCategories] = useState([]);
  const [interestCategory, setInterestCategory] = useState(null);
  const [interestProfile, setInterestProfile] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (mode === "university") setActiveTab(0);
    if (mode === "ausbildung") setActiveTab(1);
  }, [mode]);

  useEffect(() => {
    api.get("/ausbildung/categories")
      .then((response) => setManagedCategories(Array.isArray(response.data) ? response.data : []))
      .catch(() => setManagedCategories([]));
  }, []);

  const heroBackground =
    activeTab === 0
      ? "/service-university-admission.png"
      : "/service-ausbildung.png";
  const heroBackgroundPosition = activeTab === 0 ? "center 22%" : "center 30%";

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const steps = t("universityTrainingPage.process.steps", { returnObjects: true });
  const documents = t("universityTrainingPage.documents.items", { returnObjects: true });
  const universityPoints = t("universityTrainingPage.university.points", { returnObjects: true });
  const ausbildungWhyParagraphs = t("universityTrainingPage.ausbildungWhy.paragraphs", { returnObjects: true });
  const ausbildungProcessSteps = t("universityTrainingPage.ausbildungProcess.steps", { returnObjects: true });
  const isCombined = mode === "combined";
  const isAusbildungHero = activeTab === 1;
  const admissionComingSoonText =
    i18n.language === "ar"
      ? "قريبًا"
      : i18n.language === "de"
        ? "Demnächst verfügbar"
        : "Coming soon";
  const isUniversityAdmissionFrozen = activeTab === 0;
  const optionLocale = ["ar", "de"].includes(i18n.language) ? i18n.language : "en";
  const categoryImageUrl = (path) => path
    ? (path.startsWith("http") ? path : `${process.env.REACT_APP_API_URL}${path}`)
    : null;
  const handleAdmissionCtaClick = () => {
    if (isUniversityAdmissionFrozen) {
      setShowComingSoonTooltip(true);
      window.setTimeout(() => setShowComingSoonTooltip(false), 1800);
      return;
    }

    if (isLoggedIn && isStudent) {
      navigate(`/dashboard/student/submissions?type=${activeTab === 0 ? "university" : "ausbildung"}`);
    } else {
      setOpenAuthModal(true);
    }
  };

  const handleAusbildungOptionClick = (option, category) => {
    if (isLoggedIn && isStudent) {
      if (category?.status === "SOON") {
        setInterestCategory(category);
        api.get("/students/profile")
          .then((response) => setInterestProfile(response.data || null))
          .catch(() => setInterestProfile(null));
        return;
      }
      const categoryQuery = category?.id ? `&categoryId=${category.id}` : "";
      navigate(`/dashboard/student/submissions?type=ausbildung&desiredAusbildung=${encodeURIComponent(option)}${categoryQuery}`);
    } else {
      setOpenAuthModal(true);
    }
  };

  return (
    <>
      <Meta
        title="ALGermanist - University & Vocational Admission"
        description="Discover how ALGermanist helps you apply for German universities and Ausbildung programs with clear steps, document support, and Arabic guidance."
        keywords="university admission, ausbildung, vocational training, Germany, ALGermanist"
      />

      <Box>
        <Box
          sx={{
            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,.55), rgba(0,0,0,.55)), url("${heroBackground}")`,
            backgroundPosition: heroBackgroundPosition,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            color: "#fff",
            py: { xs: 8, md: isAusbildungHero ? 14 : 7 },
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                maxWidth: { xs: "100%", md: "750px" },
                textAlign: isArabic ? "right" : "left",
                pt: { xs: 3, md: isAusbildungHero ? 12 : 28 },
              }}
            >
              {isAusbildungHero && (
                <Typography sx={{ color: "#FFD600", fontWeight: 800, mb: 1, fontSize: { xs: "1rem", md: "1.15rem" } }}>
                  {t("universityTrainingPage.ausbildungHero.label")}
                </Typography>
              )}
              <Typography variant="h3" fontWeight={800} sx={{ mb: 3, lineHeight: 1.05 }}>
                {t(isAusbildungHero ? "universityTrainingPage.ausbildungHero.title" : "universityTrainingPage.hero.title")}
              </Typography>
              <Typography
                variant="h6"
                sx={{ opacity: 0.95, mb: 4, whiteSpace: "pre-line" }}
              >
                {t(isAusbildungHero ? "universityTrainingPage.ausbildungHero.subtitle" : "universityTrainingPage.hero.subtitle")}
              </Typography>
              {!isAusbildungHero && <Tooltip
                title={isUniversityAdmissionFrozen ? admissionComingSoonText : ""}
                arrow
                open={isUniversityAdmissionFrozen ? showComingSoonTooltip : false}
                onOpen={() => isUniversityAdmissionFrozen && setShowComingSoonTooltip(true)}
                onClose={() => setShowComingSoonTooltip(false)}
              >
                <span>
                  <Button
                    onClick={handleAdmissionCtaClick}
                    size="large"
                    variant="contained"
                    sx={{
                      bgcolor: "#FFD600",
                      color: "#111",
                      borderRadius: 4,
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "none",
                      cursor: isUniversityAdmissionFrozen ? "not-allowed" : "pointer",
                      "&:hover": { bgcolor: "#e5c400" },
                    }}
                  >
                    {t("universityTrainingPage.hero.cta")}
                  </Button>
                </span>
              </Tooltip>}
              <Modal open={openAuthModal} onClose={() => setOpenAuthModal(false)}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: 480 },
                    bgcolor: "background.paper",
                    borderRadius: 3,
                    p: 4,
                    outline: "none",
                  }}
                >
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                    {t("universityTrainingPage.authModal.title")}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {t("universityTrainingPage.authModal.message")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate("/login")}
                      sx={{ textTransform: "none", flex: 1 }}
                    >
                      {t("universityTrainingPage.authModal.login")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/signup")}
                      sx={{ textTransform: "none", flex: 1 }}
                    >
                      {t("universityTrainingPage.authModal.signup")}
                    </Button>
                  </Box>
                </Box>
              </Modal>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          {isCombined && (
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                centered
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  justifyContent: "center",
                }}
              >
                <Tab
                  sx={{ textTransform: "none", fontWeight: 700 }}
                  label={t("universityTrainingPage.tabs.university")}
                />
                <Tab
                  sx={{ textTransform: "none", fontWeight: 700 }}
                  label={t("universityTrainingPage.tabs.ausbildung")}
                />
              </Tabs>
            </Box>
          )}

          {activeTab === 0 && (
            <Grid container spacing={4} sx={{ pt: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
                  {t("universityTrainingPage.university.title")}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {t("universityTrainingPage.university.description")}
                </Typography>
                <List sx={{ direction: isArabic ? "rtl" : "ltr" }}>
                  {universityPoints.map((point, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemIcon sx={{ minWidth: 34, color: "#FFD600" }}>
                        <CheckCircleOutlineIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={point}
                        primaryTypographyProps={{
                          textAlign: isArabic ? "right" : "left",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    backgroundColor: "#F4F7F7",
                    borderRadius: 4,
                    p: 4,
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
                    {t("universityTrainingPage.quickInfo.title")}
                  </Typography>
                  <Typography color="text.secondary">
                    {t("universityTrainingPage.quickInfo.description")}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <>
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 3,
                  direction: isArabic ? "rtl" : "ltr",
                  textAlign: isArabic ? "right" : "left",
                  border: "1px solid #E3E7EA",
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{ px: { xs: 2.5, md: 4 }, py: 1.5, bgcolor: "#F4F7F7" }}
                >
                  <Typography variant="h5" fontWeight={900}>
                    {t("universityTrainingPage.ausbildungWhy.title")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 } }}>
                  {Array.isArray(ausbildungWhyParagraphs) && ausbildungWhyParagraphs.map((paragraph, index) => (
                    <Typography
                      key={index}
                      color="text.secondary"
                      sx={{ mb: index === ausbildungWhyParagraphs.length - 1 ? 0 : 2, lineHeight: 1.85 }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>

              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 2,
                  direction: isArabic ? "rtl" : "ltr",
                  textAlign: isArabic ? "right" : "left",
                  border: "1px solid #E3E7EA",
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon />}
                  sx={{ px: { xs: 2.5, md: 4 }, py: 1.5, bgcolor: "#F4F7F7" }}
                >
                  <Typography variant="h5" fontWeight={900}>
                    {t("universityTrainingPage.ausbildungProcess.title")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 } }}>
                  <Grid container spacing={2}>
                    {Array.isArray(ausbildungProcessSteps) && ausbildungProcessSteps.map((step, index) => (
                      <Grid item xs={12} md={index < 3 ? 4 : 6} key={step.title}>
                        <Box sx={{ height: "100%", p: 2.5, borderRadius: 3, bgcolor: "#F8FAFA" }}>
                          <Typography sx={{ color: "#C7A900", fontWeight: 900, mb: 0.75 }}>
                            {String(index + 1).padStart(2, "0")}
                          </Typography>
                          <Typography fontWeight={800} sx={{ mb: 0.75 }}>
                            {step.title}
                          </Typography>
                          <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            {step.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Box sx={{ mt: { xs: 4, md: 6 }, textAlign: isArabic ? "right" : "left" }}>
                <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>
                  {isArabic
                    ? "في أي مجال ترغب في بدء تدريبك المهني؟"
                    : i18n.language === "de"
                      ? "In welchem Bereich möchtest du deine Ausbildung machen?"
                      : "Which field would you like to pursue your vocational training in?"}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 760, ml: isArabic ? "auto" : 0 }}>
                  {isArabic
                    ? "اختر المجال لعرض التفاصيل والمتطلبات وبدء طلبك."
                    : i18n.language === "de"
                      ? "Wähle einen Bereich, um Details, Voraussetzungen und die Bewerbung zu öffnen."
                      : "Choose a field to view its details, requirements, and application."}
                </Typography>
                <Grid container spacing={3}>
                  {(managedCategories.length ? managedCategories.map((category) => category.name) : ausbildungOptions).map((option) => {
                    const category = managedCategories.find((item) => item.name === option);
                    const detail = ausbildungOptionDetails[option] || {
                      image: "/service-ausbildung.png",
                      availability: category?.status === "OPEN" ? "open" : "soon",
                      action: category?.status === "OPEN" ? "apply" : "interest",
                      en: { title: option, description: "Vocational training opportunity in Germany." },
                      de: { title: option, description: "Ausbildungsmöglichkeit in Deutschland." },
                      ar: { title: option, description: "فرصة تدريب مهني في ألمانيا." },
                    };
                    const localeSuffix = optionLocale === "ar" ? "Ar" : optionLocale === "de" ? "De" : "En";
                    const fallbackText = detail[optionLocale] || detail.en;
                    const text = {
                      ...fallbackText,
                      description: category?.[`description${localeSuffix}`] || fallbackText.description,
                    };
                    const managedRequirements = category?.[`requirements${localeSuffix}`];
                    const fallbackRequirements = [
                      category?.requiredLanguageLevel && `${optionLocale === "ar" ? "مستوى اللغة" : optionLocale === "de" ? "Sprachniveau" : "Language level"}: ${category.requiredLanguageLevel}`,
                      category?.ageRequirement && `${optionLocale === "ar" ? "العمر" : optionLocale === "de" ? "Alter" : "Age"}: ${category.ageRequirement}`,
                    ].filter(Boolean).join("\n");
                    const requirements = managedRequirements || fallbackRequirements;
                    const cardImage = categoryImageUrl(category?.imagePath) || detail.image;
                    const isOpen = category ? category.status === "OPEN" : detail.availability === "open";
                    const statusText = isArabic
                      ? (isOpen ? "وظائف متاحة حاليًا" : "سيتوفر مجددًا قريبًا")
                      : i18n.language === "de"
                        ? (isOpen ? "Aktuell offene Stellen" : "Bald wieder verfügbar")
                        : (isOpen ? "Positions currently available" : "Available again soon");
                    const buttonText = isArabic
                      ? (!isOpen ? "سجّل اهتمامك" : "قدّم الآن")
                      : i18n.language === "de"
                        ? (!isOpen ? "Interesse hinterlassen" : "Jetzt bewerben")
                        : (!isOpen ? "Register interest" : "Apply now");

                    return (
                      <Grid item xs={12} sm={6} md={2.4} key={option}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => handleAusbildungOptionClick(option, category)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleAusbildungOptionClick(option, category);
                            }
                          }}
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: 3,
                            border: "1px solid #E3E7EA",
                            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFB 100%)",
                            boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
                            cursor: "pointer",
                            transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
                            outline: "none",
                            "&:hover, &:focus-visible": {
                              transform: "translateY(-5px)",
                              borderColor: "#FFD600",
                              boxShadow: "0 18px 42px rgba(15, 23, 42, 0.14)",
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={cardImage}
                            alt={text.title}
                            sx={{
                              width: "100%",
                              height: 175,
                              objectFit: "cover",
                              borderRadius: "12px 12px 0 0",
                            }}
                          />
                          <Box sx={{ p: 2.25, display: "flex", flexDirection: "column", flex: 1 }}>
                            <Chip
                              label={statusText}
                              size="small"
                              sx={{
                                alignSelf: isArabic ? "flex-end" : "flex-start",
                                mb: 1.5,
                                bgcolor: isOpen ? "#E7F7ED" : "#FFF4CC",
                                color: isOpen ? "#16733A" : "#725C00",
                                fontWeight: 800,
                              }}
                            />
                            <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
                              {text.title}
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2, flex: 1 }}>
                              {text.description}
                            </Typography>
                            {requirements && <Box sx={{ mb: 2, p: 1.25, borderRadius: 2, bgcolor: "rgba(15, 23, 42, 0.04)" }}>
                              <Typography variant="caption" fontWeight={900} sx={{ display: "block", mb: 0.5 }}>
                                {optionLocale === "ar" ? "المتطلبات" : optionLocale === "de" ? "Voraussetzungen" : "Requirements"}
                              </Typography>
                              {requirements.split(/\r?\n/).filter(Boolean).map((line, index) => <Typography key={`${option}-${index}`} variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                • {line}
                              </Typography>)}
                            </Box>}
                            <Typography fontWeight={900} sx={{ color: "#111" }}>
                              {buttonText}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </>
          )}
        </Container>

        {activeTab === 0 && (
          <>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 3, textAlign: isArabic ? "right" : "left" }}>
            {t("universityTrainingPage.process.title")}
          </Typography>
          <Grid container spacing={3}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Box
                  sx={{
                    backgroundColor: "#F8FAFB",
                    borderRadius: 4,
                    p: 4,
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                    {t("universityTrainingPage.process.stepLabel", { step: index + 1 })}
                  </Typography>
                  <Typography color="text.secondary">{step}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Box sx={{ borderRadius: 4, p: 4, backgroundColor: "#F4F7F7" }}>
                <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>
                  {t("universityTrainingPage.documents.title")}
                </Typography>
                <List sx={{ direction: isArabic ? "rtl" : "ltr" }}>
                  {documents.map((item, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemIcon sx={{ minWidth: 34, color: "#FFD600" }}>
                        <CheckCircleOutlineIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{
                          textAlign: isArabic ? "right" : "left",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
            {/*
              Frozen for now: "Need help preparing your file?" consultation CTA.
            <Grid item xs={12} md={6}>
              <Box sx={{ borderRadius: 4, p: 4, backgroundColor: "#fff", border: "1px solid #E3E7EA" }}>
                <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
                  {t("universityTrainingPage.documents.helpTitle")}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 4 }}>
                  {t("universityTrainingPage.documents.helpDescription")}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate("/career-consultation")}
                  sx={{
                    backgroundColor: "#FFD600",
                    color: "#111",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 4,
                    px: 4,
                    py: 1.5,
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#e5c400" },
                  }}
                >
                  {isArabic ? "احجز استشارتك المجانية الأولى" : "Book your first free consultation"}
                </Button>
              </Box>
            </Grid>
            */}
          </Grid>
        </Container>
          </>
        )}
      </Box>
      <Dialog
        open={Boolean(interestCategory)}
        onClose={() => setInterestCategory(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: isArabic ? "flex-start" : "flex-end", pb: 0 }}>
          <IconButton
            onClick={() => setInterestCategory(null)}
            aria-label={isArabic ? "إغلاق" : "Close"}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {interestCategory && (
            <AusbildungInterestForm
              categoryId={interestCategory.id}
              categoryName={interestCategory.name}
              profile={interestProfile}
              isArabic={isArabic}
              language={i18n.language?.split("-")[0]}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UniversityTrainingPage;
